import { FormEvent, useCallback, useState } from 'react'
import { motion, useAnimation } from 'framer-motion'
import {
  AiinsightspbPostAIInsightsFeedbackRequestThumbRatingEnum,
  type AiinsightspbPostAIInsightsFeedbackRequest
} from '@itsrever/rever-api'
import { Base, ThumbIcon } from '@itsrever/design-system'
import { useReturnRateInsightFeedback } from '@/hooks/use-return-rate-insight-feedback'
import { useEcommerceId } from '@/hooks'
import { useTranslation } from 'react-i18next'

const {
  PopoverRoot,
  PopoverArrow,
  PopoverPortal,
  PopoverPositioner,
  PopoverTrigger,
  PopoverPopup,
  Textarea,
  Button
} = Base

interface ReturnRateAIInsightsFeedbackProps {
  insightId?: number
}

export const ReturnRateAIInsightsFeedback = ({
  insightId
}: ReturnRateAIInsightsFeedbackProps) => {
  const [open, setOpen] = useState(false)

  const ecommerce_id = useEcommerceId()

  const { mutate, variables } = useReturnRateInsightFeedback()

  const saveFeedback = ({
    thumb_rating,
    feedback_message
  }: Pick<
    AiinsightspbPostAIInsightsFeedbackRequest,
    'feedback_message' | 'thumb_rating'
  >) => {
    if (!ecommerce_id) {
      return
    }

    mutate({
      aiinsightspbPostAIInsightsFeedbackRequest: {
        ecommerce_id,
        insight_id: insightId,
        feedback_message,
        thumb_rating
      }
    })
  }

  return (
    <div className="flex gap-0.5">
      <button
        data-selected={
          variables?.aiinsightspbPostAIInsightsFeedbackRequest.thumb_rating ===
          'THUMB_RATING_THUMBS_UP'
        }
        disabled={
          variables?.aiinsightspbPostAIInsightsFeedbackRequest.thumb_rating ===
          'THUMB_RATING_THUMBS_UP'
        }
        className="size-7 rounded-md text-neutral-600 hover:bg-gray-100 hover:text-neutral-900 data-[selected=true]:pointer-events-none data-[selected=true]:bg-gray-200/60 data-[selected=true]:text-neutral-900"
        onClick={() =>
          saveFeedback({
            thumb_rating:
              AiinsightspbPostAIInsightsFeedbackRequestThumbRatingEnum.ThumbsUp
          })
        }
        aria-label="Thumbs up"
      >
        <AnimatedThumb direction="up" />
      </button>
      <PopoverRoot onOpenChange={setOpen} open={open}>
        <PopoverTrigger
          render={
            <button
              data-selected={
                variables?.aiinsightspbPostAIInsightsFeedbackRequest
                  .thumb_rating === 'THUMB_RATING_THUMBS_DOWN'
              }
              disabled={
                variables?.aiinsightspbPostAIInsightsFeedbackRequest
                  .thumb_rating === 'THUMB_RATING_THUMBS_DOWN'
              }
              className="size-7 rounded-md text-neutral-600 hover:bg-gray-100 hover:text-neutral-900 data-[selected=true]:pointer-events-none data-[selected=true]:bg-gray-200/60  data-[selected=true]:text-neutral-900"
              aria-label="Thumbs down"
            />
          }
        >
          <AnimatedThumb direction="down" />
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverPositioner sideOffset={8}>
            <PopoverPopup className="p-4">
              <PopoverArrow />
              <FeedbackForm
                onOpenChange={setOpen}
                onChangeFeedback={saveFeedback}
              />
            </PopoverPopup>
          </PopoverPositioner>
        </PopoverPortal>
      </PopoverRoot>
    </div>
  )
}

interface FeedbackFormProps {
  onOpenChange: (open: boolean) => void
  onChangeFeedback: (
    values: Pick<
      AiinsightspbPostAIInsightsFeedbackRequest,
      'feedback_message' | 'thumb_rating'
    >
  ) => void
}

const FeedbackForm = ({
  onOpenChange,
  onChangeFeedback
}: FeedbackFormProps) => {
  const { t } = useTranslation()

  const submitFeedbback = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)

    onChangeFeedback({
      feedback_message: (formData.get('feedback') as string) ?? undefined,
      thumb_rating: 'THUMB_RATING_THUMBS_DOWN'
    })

    onOpenChange(false)
  }

  return (
    <form className="w-80 space-y-1.5" onSubmit={submitFeedbback}>
      <div className="space-y-1">
        <label htmlFor="feedback" className="text-sm">
          {t('returns_analytics.ai_insights_feedback_label')}
        </label>
        <Textarea
          name="feedback"
          id="feedback"
          placeholder={t('returns_analytics.ai_insights_feedback_placeholder')}
          className="h-24 resize-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          className="w-full"
          type="button"
          onClick={() => onOpenChange(false)}
        >
          {t('returns_analytics.ai_insights_feedback_cancel')}
        </Button>
        <Button variant="primary" className="w-full">
          {t('returns_analytics.ai_insights_feedback_submit')}
        </Button>
      </div>
    </form>
  )
}

// The thumb tilts towards its direction on hover.
const AnimatedThumb = ({ direction }: { direction: 'up' | 'down' }) => {
  const controls = useAnimation()

  const handleMouseEnter = useCallback(() => {
    controls.start('animate')
  }, [controls])

  const handleMouseLeave = useCallback(() => {
    controls.start('normal')
  }, [controls])

  return (
    <span
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="flex size-full items-center justify-center"
    >
      <motion.span
        className="flex"
        variants={{
          normal: {
            translateX: '0px',
            translateY: '0px',
            rotate: '0deg'
          },
          animate: {
            translateX: '-1px',
            translateY: direction === 'up' ? '-2px' : '2px',
            rotate: '-12deg'
          }
        }}
        animate={controls}
        transition={{ type: 'spring', stiffness: 250, damping: 25 }}
      >
        <ThumbIcon direction={direction} />
      </motion.span>
    </span>
  )
}
